import { lazy, Suspense } from 'react';
import { Navigate, Routes as ReactRoutes, Route } from 'react-router-dom';
import { ChannelFocusLayout, Layout, PartnerScoreLayout, ProtectedRoute } from './components/base';
import FeatureFlagRoute from './components/base/FeatureFlagRoute/FeatureFlagRoute';

import { UserProfileManager } from '@/api/userProfile/UserProfileManager';
import { useUserProfile } from '@/hooks';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import CompatibilityRedirects from './components/navigation/CompatibilityRedirects/CompatibilityRedirects';
import SalesAccessRoute from './pages/Sales/SalesAccessRoute';
import SalesIndexRedirect from './pages/Sales/SalesIndexRedirect';
import { AppRoutes } from './utils';

const CustomerFocus = lazy(() => import('./pages/CustomerFocus/CustomerFocus'));
const PersonalizedEngagement = lazy(() => import('./pages/PersonalizedEngagement/PersonalizedEngagementPage'));
const SearchHcps = lazy(() => import('./pages/SearchHealthCareProfessionals/SearchHealthCareProfessionals'));
const MyHCPs = lazy(() => import('./pages/MyHCPs/MyHCPs'));

const PartnerScore = lazy(() => import('./pages/PartnerScore/PartnerScore'));
const CesScore = lazy(() => import('./pages/CesScore/CesScore'));
const CsatScore = lazy(() => import('./pages/CsatScore/CsatScore'));
const NpsScore = lazy(() => import('./pages/NpsScore/NpsScore'));

const HCPOverview = lazy(() => import('./pages/HCPOverview/HCPOverview'));
const HCOOverview = lazy(() => import('./pages/HCOOverview/HCOOverview'));
const DataRefreshDetails = lazy(() => import('./pages/DataRefreshDetails/DataRefreshDetails'));
const PerformanceDashboard = lazy(() => import('./pages/PerformanceDashboard/PerformanceDashboard'));
const HcpClinicalTrialDetails = lazy(() => import('./pages/HcpClinicalTrialDetails/HcpClinicalTrialDetails'));

const AllChannel = lazy(() => import('./pages/ChannelFocus/AllChannel'));
const EmailChannelFocus = lazy(() => import('./pages/ChannelFocus/EmailChannelFocus'));
const FaceToFaceChannelFocus = lazy(() => import('./pages/ChannelFocus/FaceToFaceChannelFocus'));
const EventChannelFocus = lazy(() => import('./pages/ChannelFocus/EventChannelFocus'));
const WebsiteChannelFocus = lazy(() => import('./pages/ChannelFocus/WebsiteChannelFocus'));

const NotFound = lazy(() => import('./pages/NotFound/NotFound'));
const Disclaimer = lazy(() => import('./pages/Disclaimer/Disclaimer'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage/DisclaimerPage'));
const Login = lazy(() => import('./pages/Login/Login'));
const ManagePreferencesLayout = lazy(() => import('./pages/ManagePreferences/ManagePreferencesLayout'));
const ManagePreferencesUser = lazy(() => import('./pages/ManagePreferences/ManagePreferencesUser'));
const ManagePreferencesLanguage = lazy(() => import('./pages/ManagePreferences/ManagePreferencesLanguage'));
const AccessDeniedPage = lazy(() => import('./pages/AccessDeniedPage/AccessDeniedPage'));

const CompetitorAnalysisLayout = lazy(() => import('./pages/CompetitorAnalysis/CompetitorAnalysisLayout'));
const CompetitorPartnerScore = lazy(() => import('./pages/CompetitorAnalysis/components/PartnerScore/PartnerScore'));
const CompetitorCsatScore = lazy(() => import('./pages/CompetitorAnalysis/components/CsatScore/CsatScore'));
const CompetitorEffortScore = lazy(() => import('./pages/CompetitorAnalysis/components/EffortScore/EffortScore'));
const CompetitorNetPromoter = lazy(() => import('./pages/CompetitorAnalysis/components/NetPromoter/NetPromoter'));

const GlobalBenchmarking = lazy(() => import('./pages/GlobalBenchmarking/GlobalBenchmarking'));
const InsightsAnalysis = lazy(() => import('./pages/Insights/InsightsAnalysis'));
const InsightsKPIs = lazy(() => import('./pages/Insights/InsightsKPIs'));
const InsightsLayout = lazy(() => import('./pages/Insights/InsightsLayout'));
const AggregatedConsent = lazy(() => import('./pages/AggregatedConsent/AggregatedConsent'));
const AggregatedScientificInformationLayout = lazy(
  () => import('./pages/AggregatedScientificInformation/AggregatedScientificInformationLayout'),
);
const AggregatedScientificInformationIndexRedirect = lazy(
  () => import('./pages/AggregatedScientificInformation/AggregatedScientificInformationIndexRedirect'),
);
const ComingSoonPlaceholder = lazy(() => import('./pages/AggregatedScientificInformation/ComingSoonPlaceholder'));
const AggregatedScientificInformationClinicalTrials = lazy(
  () => import('./pages/AggregatedScientificInformation/tabs/ClinicalTrials/ClinicalTrials'),
);
const HcpCongressAbstractDetails = lazy(() => import('./pages/HcpCongressAbstractDetails/HcpCongressAbstractDetails'));
const ConferenceOnePager = lazy(() => import('./pages/ConferenceOnePager/ConferenceOnePager'));

const SalesLayout = lazy(() => import('./pages/Sales/SalesLayout'));
const SalesKeyIndicators = lazy(() => import('./pages/Sales/tabs/KeyIndicators/KeyIndicators'));
const SalesRegionalFocus = lazy(() => import('./pages/Sales/tabs/RegionalFocus/RegionalFocus'));
const SalesCompetitorFocus = lazy(() => import('./pages/Sales/tabs/CompetitorFocus/CompetitorFocus'));

const Routes = () => {
  const user = useUserProfile();
  const openHomepage = user?.disclaimerRead;
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ReactRoutes>
        <Route element={<CompatibilityRedirects />}>
          <Route path="/" element={<Login />} />
          <Route path={AppRoutes.ACCESS_DENIED} element={<AccessDeniedPage />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<UserProfileManager />}>
              <Route element={<Layout />}>
                {!openHomepage ? (
                  <Route path="/*" element={<Disclaimer />} />
                ) : (
                  <>
                    <Route
                      path={AppRoutes.MANAGE_PREFERENCES}
                      element={<Navigate to={AppRoutes.MANAGE_PREFERENCES_USER} replace />}
                    />
                    <Route element={<ManagePreferencesLayout />}>
                      <Route path={AppRoutes.MANAGE_PREFERENCES_USER} element={<ManagePreferencesUser />} />
                      <Route path={AppRoutes.MANAGE_PREFERENCES_LANGUAGE} element={<ManagePreferencesLanguage />} />
                    </Route>
                    <Route path={AppRoutes.CUSTOMER_FOCUS} element={<CustomerFocus />} />
                    <Route path={AppRoutes.PERSONALIZED_ENGAGEMENT} element={<PersonalizedEngagement />} />
                    <Route path={AppRoutes.SEARCH_HCPS} element={<SearchHcps />} />
                    <Route path={AppRoutes.MY_HCPS} element={<MyHCPs />} />
                    <Route path={AppRoutes.HCP_OVERVIEW} element={<HCPOverview />} />
                    <Route path={AppRoutes.HCO_OVERVIEW} element={<HCOOverview />} />
                    <Route path={AppRoutes.HCP_CLINICAL_TRIAL_DETAILS} element={<HcpClinicalTrialDetails />} />
                    <Route path={AppRoutes.HCP_CONFERENCE_ABSTRACT_DETAILS} element={<HcpCongressAbstractDetails />} />
                    <Route path={AppRoutes.CONFERENCE_ONE_PAGER} element={<ConferenceOnePager />} />
                    <Route path={AppRoutes.DATA_REFRESH_DETAILS} element={<DataRefreshDetails />} />
                    <Route path={AppRoutes.DISCLAIMER_PAGE} element={<DisclaimerPage />} />
                    <Route path={AppRoutes.PERFORMANCE_DASHBOARD} element={<PerformanceDashboard />} />
                    <Route
                      path={AppRoutes.PARTNERSCORE_GROUP}
                      element={<Navigate to={AppRoutes.PARTNERSCORE} replace />}
                    />
                    <Route element={<PartnerScoreLayout />}>
                      <Route path={AppRoutes.PARTNERSCORE} element={<PartnerScore />} />
                      <Route path={AppRoutes.CSATSCORE} element={<CsatScore />} />
                      <Route path={AppRoutes.CESSCORE} element={<CesScore />} />
                      <Route path={AppRoutes.NPSSCORE} element={<NpsScore />} />
                    </Route>

                    <Route
                      path={AppRoutes.COMPETITOR_ANALYSIS_GROUP}
                      element={<Navigate to={AppRoutes.COMPETITOR_ANALYSIS} replace />}
                    />
                    <Route element={<CompetitorAnalysisLayout />}>
                      <Route path={AppRoutes.COMPETITOR_ANALYSIS} element={<CompetitorPartnerScore />} />
                      <Route path={AppRoutes.COMPETITOR_CSATSCORE} element={<CompetitorCsatScore />} />
                      <Route path={AppRoutes.COMPETITOR_CESSCORE} element={<CompetitorEffortScore />} />
                      <Route path={AppRoutes.COMPETITOR_NPSSCORE} element={<CompetitorNetPromoter />} />
                    </Route>

                    <Route path="aggregated-consent/:channel" element={<AggregatedConsent />} />
                    <Route path="aggregated-consent" element={<AggregatedConsent />} />

                    <Route
                      path={AppRoutes.GLOBAL_BENCHMARKING_GROUP}
                      element={<Navigate to={AppRoutes.GLOBAL_BENCHMARKING_1_TO_1} replace />}
                    />
                    <Route path="global-benchmarking/:channel" element={<GlobalBenchmarking />} />

                    <Route
                      path={AppRoutes.CHANNEL_FOCUS_GROUP}
                      element={<Navigate to={AppRoutes.CHANNEL_ALL_CHANNELS} replace />}
                    />
                    <Route element={<ChannelFocusLayout />}>
                      <Route path={AppRoutes.CHANNEL_ALL_CHANNELS} element={<AllChannel />} />
                      <Route path={AppRoutes.CHANNEL_FOCUS_1_TO_1} element={<FaceToFaceChannelFocus />} />
                      <Route path={AppRoutes.CHANNEL_FOCUS_EMAIL} element={<EmailChannelFocus />} />
                      <Route path={AppRoutes.CHANNEL_FOCUS_EVENT} element={<EventChannelFocus />} />
                      <Route path={AppRoutes.CHANNEL_FOCUS_WEBSITE} element={<WebsiteChannelFocus />} />
                    </Route>

                    <Route
                      path={AppRoutes.INSIGHTS_GROUP}
                      element={<Navigate to={AppRoutes.INSIGHTS_ANALYSIS} replace />}
                    />
                    <Route element={<InsightsLayout />}>
                      <Route path={AppRoutes.INSIGHTS_ANALYSIS} element={<InsightsAnalysis />} />
                      <Route path={AppRoutes.INSIGHTS_KPIS} element={<InsightsKPIs />} />
                    </Route>

                    <Route element={<FeatureFlagRoute feature="aggregatedScientificInformation" />}>
                      <Route
                        path={AppRoutes.AGGREGATED_SCIENTIFIC_INFORMATION_GROUP}
                        element={<AggregatedScientificInformationIndexRedirect />}
                      />
                      <Route element={<AggregatedScientificInformationLayout />}>
                        <Route
                          path={AppRoutes.AGGREGATED_SCIENTIFIC_INFORMATION_OVERVIEW}
                          element={<ComingSoonPlaceholder />}
                        />
                        <Route
                          path={AppRoutes.AGGREGATED_SCIENTIFIC_INFORMATION_CLINICAL_TRIALS}
                          element={<AggregatedScientificInformationClinicalTrials />}
                        />
                        <Route
                          path={AppRoutes.AGGREGATED_SCIENTIFIC_INFORMATION_PUBLICATIONS}
                          element={<ComingSoonPlaceholder />}
                        />
                        <Route
                          path={AppRoutes.AGGREGATED_SCIENTIFIC_INFORMATION_CONGRESS_ACTIVITY}
                          element={<ComingSoonPlaceholder />}
                        />
                      </Route>
                    </Route>

                    <Route element={<FeatureFlagRoute feature="sales" />}>
                      <Route element={<SalesAccessRoute />}>
                        <Route path={AppRoutes.SALES_GROUP} element={<SalesIndexRedirect />} />
                        <Route element={<SalesLayout />}>
                          <Route path={AppRoutes.SALES_KEY_INDICATORS} element={<SalesKeyIndicators />} />
                          <Route path={AppRoutes.SALES_REGIONAL_FOCUS} element={<SalesRegionalFocus />} />
                          <Route path={AppRoutes.SALES_COMPETITOR_FOCUS} element={<SalesCompetitorFocus />} />
                        </Route>
                      </Route>
                    </Route>

                    <Route path="/*" element={<NotFound />} />
                  </>
                )}
              </Route>
            </Route>
          </Route>
        </Route>
      </ReactRoutes>
    </Suspense>
  );
};

export default Routes;
